import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  CoverageLanguage,
  EEntityType,
  GetTranslationEntitiesParams,
  LanguageListItem,
  PaginatedResponse,
  Pagination,
  TranslationEntityItem,
  TranslationRecord,
  UpsertTranslationDto,
  typicodeApi,
} from 'shared/api';

type TTranslationState = {
  languages: LanguageListItem[];
  coverage: CoverageLanguage[];
  entities: TranslationEntityItem[];
  pagination: Pagination | null;
};

const initialState: TTranslationState = {
  languages: [],
  coverage: [],
  entities: [],
  pagination: null,
};

export const translationSlice = createSlice({
  name: 'translation',
  initialState,
  reducers: {
    setLanguages: (state, { payload }: PayloadAction<LanguageListItem[]>) => {
      state.languages = payload;
    },
    setCoverage: (state, { payload }: PayloadAction<CoverageLanguage[]>) => {
      state.coverage = payload;
    },
    setEntities: (state, { payload }: PayloadAction<TranslationEntityItem[]>) => {
      state.entities = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
    resetEntities: (state) => {
      state.entities = [];
      state.pagination = null;
    },
  },
});

export const { resetEntities } = translationSlice.actions;

export const useTranslationState = () => useSelector((state: RootState) => state.translations);

export const reducer = translationSlice.reducer;

// ===== React Query hooks =====

const LANGUAGES_QUERY_KEY = ['translation', 'languages'] as const;
const COVERAGE_QUERY_KEY = ['translation', 'coverage'] as const;
const ENTITIES_QUERY_KEY = 'translationEntities';

export const getLanguagesAsync = () => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<LanguageListItem[]>>(LANGUAGES_QUERY_KEY, () => typicodeApi.translations.getLanguages(), {
    onSuccess: ({ data }) => {
      dispatch(translationSlice.actions.setLanguages(data));
    },
    onError: () => {},
    refetchOnWindowFocus: false,
  });

export const getCoverageMatrixAsync = () => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<CoverageLanguage[]>>(COVERAGE_QUERY_KEY, () => typicodeApi.translations.getCoverageMatrix(), {
    onSuccess: ({ data }) => {
      dispatch(translationSlice.actions.setCoverage(data));
    },
    onError: () => {},
    refetchOnWindowFocus: false,
  });

export const getEntitiesAsync =
  (entityType: EEntityType, params: GetTranslationEntitiesParams) => (dispatch: Dispatch) =>
    useQuery<AxiosResponse<PaginatedResponse<TranslationEntityItem>>>(
      [ENTITIES_QUERY_KEY, entityType, params],
      () => typicodeApi.translations.getEntities(entityType, params),
      {
        enabled: Boolean(entityType && params.language),
        onSuccess: ({ data }) => {
          dispatch(translationSlice.actions.setEntities(data.data));
          dispatch(translationSlice.actions.setPagination(data.pagination));
        },
        onError: () => {},
        refetchOnWindowFocus: false,
        keepPreviousData: true,
      },
    );

export const upsertTranslationAsync = () => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<TranslationRecord>, unknown, UpsertTranslationDto>(
    (dto) => typicodeApi.translations.upsert(dto),
    {
      onSuccess: () => {
        toast.success('Translation saved');
        void queryClient.invalidateQueries(ENTITIES_QUERY_KEY);
        void queryClient.invalidateQueries(COVERAGE_QUERY_KEY);
      },
      onError: () => {
        toast.error('Failed to save translation. Try again.');
      },
    },
  );
};
